import { Box } from '@mui/material';
import { useProfileContext } from '../../profile.context';
import { ImageCropper, createImage, downloadBlob, fileToUrl, getCroppedImg, PhotoDropzone, useAlert } from '@uplift/ui-v2';
import { useState } from 'react';
import { StepFormContainer } from '../StepFormContainer.component';
import { ACCEPTABLE_IMAGE_FORMATS_LIST, MAX_IMAGE_UPLOAD_SIZE } from '@uplift/constants';
import { useL10n } from '@uplift/hooks';
import { Area } from 'react-easy-crop/types';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useUpdateAvatar } from '@uplift/queries';
import { useUploadAvatar } from '@uplift/utils';
import { PROFILE_QUERY_KEY } from '../../profile.utils';
import { PhotoDescription } from './PhotoDescription.component';

export type PhotoStepProps = {
  onBack: () => void;
  onNext: () => void;
};

export const PhotoStep: React.VFC<PhotoStepProps> = props => {
  const { profile } = useProfileContext();
  const [ isUploadStep, setIsUploadStep ] = useState(true);
  const alert = useAlert();
  const l10n = useL10n();
  const queryClient = useQueryClient();
  const { updateAvatar } = useUpdateAvatar(profile.user.id);
  const uploadAvatar = useUploadAvatar();
  const [ imageName, setImageName ] = useState<string | undefined>();
  const [ image, setImage ] = useState<string | undefined>(profile.avatarAssetOriginal?.url);
  const [ cropArea, setCropArea ] = useState<Area | undefined>();
  const uploadMutation = useMutation({
    mutationFn: async () => {
      if (!image || !cropArea || !imageName) {
        alert.error(l10n.validations.photoUpload);
        return;
      }

      const croppedImage = await getCroppedImg(image, cropArea);

      const imageCroppedFile = await downloadBlob(croppedImage);
      const assetCropped = await uploadAvatar(imageCroppedFile, imageName);

      const imageOriginalFile = await downloadBlob(image);
      const assetOriginal = await uploadAvatar(imageOriginalFile, imageName);

      const updateAvatarRes = await updateAvatar({
        avatarAssetId: assetCropped,
        originalAvatarAssetId: assetOriginal,
        avatarAssetIdToUpdate: profile.avatarAsset?.id,
        originalAvatarAssetIdToUpdate: profile.avatarAssetOriginal?.id
      });

      return updateAvatarRes;
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries(PROFILE_QUERY_KEY(profile.user.id));
      props.onNext();
    }
  });

  const onFileUpload = async (file: File) => {
    if (!file) {
      return;
    }

    if (file.size > MAX_IMAGE_UPLOAD_SIZE) {
      alert.error(l10n.validations.imageTooBig);
      return;
    }

    const fileUrl = fileToUrl(file);
    const img = await createImage(fileUrl);

    if (img.width < 250) {
      alert.error(l10n.validations.imageWidthTooBig);
      return;
    }

    if (!ACCEPTABLE_IMAGE_FORMATS_LIST.includes(file.type)) {
      alert.error(l10n.validations.invalidFileType);
      return;
    }

    setImageName(file.name);
    setImage(fileToUrl(file));
  };

  const onCropComplete = async (_: Area, croppedAreaPixels: Area) => {
    setCropArea(croppedAreaPixels);
  };

  const onSetCropStep = () => {
    if (!image) {
      // this step is optional, so we can skip it
      props.onNext();
      return;
    }

    setIsUploadStep(false);
  };

  if (isUploadStep) {
    return (
      <StepFormContainer
        title="Upload a photo of yourself"
        description={(
          <Box mt={4}>
            <PhotoDescription />
          </Box>
        )}
        form={(
          <Box height="300px">
            <PhotoDropzone image={image} onDropAccepted={onFileUpload} />
          </Box>
        )}
        onBack={props.onBack}
        onNext={onSetCropStep}
      />
    );
  }

  return (
    <StepFormContainer
      onBack={() => setIsUploadStep(true)}
      onNext={uploadMutation.mutate}
      isLoading={uploadMutation.isLoading}
      title="Upload a photo of yourself"
      form={(
        <ImageCropper
          imageUrl={image}
          onCropComplete={onCropComplete}
        />
      )}
    />
  );
};
